'use client';

import '@pickleballinc/ui/globals.css';

import { DateTimePicker, DateTimePickerProps } from '@pickleballinc/ui/components/ui/datetime-picker';
import { Controller, useFormContext } from 'react-hook-form';
import { DateUtil } from '@/utils/date';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DatePickerFormProps extends Omit<DateTimePickerProps, 'onChange' | 'value' | 'className'> {
	name: string;
	placeholder?: string;
	className?: string;
	ianaTzIdentifier?: string;
	defaultValue?: string | null;
}

export function DatetimePickerForm({ name, placeholder = 'select...', className, ianaTzIdentifier, defaultValue, ...props }: DatePickerFormProps) {
	const { control, watch } = useFormContext();
	const [selectedDate, setSelectedDate] = useState<Date | undefined>();

	useEffect(() => {
		const value = watch(name);
		if (value) {
			const date = ianaTzIdentifier ? new Date(DateUtil.convertUTCToTimezone(value, ianaTzIdentifier)) : new Date(value);
			setSelectedDate(date);
		} else {
			setSelectedDate(undefined);
		}
	}, [watch(name), ianaTzIdentifier]);

	return (
		<Controller
			defaultValue={defaultValue}
			control={control}
			name={name}
			render={({ field: { onChange } }) => {
				const handleDateChange = (date: Date | undefined) => {
					setSelectedDate(date);
					if (date) {
						if (ianaTzIdentifier) {
							onChange(DateUtil.convertInTimezoneUTC(date, ianaTzIdentifier));
						} else {
							onChange(DateUtil.toISOStringWithOffset(date));
						}
					} else {
						onChange(undefined);
					}
				};

				return <DateTimePicker value={selectedDate} onChange={handleDateChange} placeholder={placeholder} className={className} {...props} />;
			}}
		/>
	);
}
